import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { useModalA11y } from '@/hooks/useModalA11y'
import styles from './ConfirmDialog.module.css'

interface ConfirmDialogProps {
  open: boolean
  title: string
  message: ReactNode
  confirmLabel: string
  cancelLabel?: string
  /** Red confirm button + danger icon (destructive actions). */
  danger?: boolean
  /** Disables both buttons while the action is in flight. */
  busy?: boolean
  /**
   * Focus target on close when the opener has left the page — a row the
   * confirmed action removed (useModalA11y's `returnFocus`). A host that opens
   * the dialog from a menu item focuses the menu's trigger before the menu
   * unmounts, so the trigger is the opener the hook saves.
   */
  returnFocusRef?: RefObject<HTMLElement>
  onConfirm: () => void
  onCancel: () => void
}

// Exits take the next duration down from the 320ms entrance (design system
// Motion); keep in step with `--dur-med` on `.closing` in the module CSS.
const EXIT_MS = 240

/**
 * Generic "Are you sure?" confirmation dialog. Used for destructive/irreversible
 * actions (e.g. releasing a Twilio number, deleting a subscription plan).
 * Click-outside, Escape, and Cancel all dismiss without confirming; focus is
 * trapped and restored via useModalA11y (DES-08). While `busy`, dismiss is
 * blocked but the trap/scroll-lock stay active — passing `open && !busy` into
 * the hook used to tear the trap down mid-flight (Bugbot).
 *
 * It portals to <body>: a transformed or size-contained ancestor (the Brain
 * history pane slides on a transform) otherwise becomes the containing block
 * of the fixed scrim and traps it inside that pane. On close it stays mounted,
 * inert, for a short exit showing what was on screen — callers usually clear
 * the target that feeds the title and message.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  returnFocusRef,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Ref so the a11y hook's onClose identity stays stable across busy flips
  // (a changing callback would re-run the effect and restore focus under the
  // still-visible overlay).
  const busyRef = useRef(busy)
  busyRef.current = busy
  const handleClose = useCallback(() => {
    if (!busyRef.current) onCancel()
  }, [onCancel])
  const dialogRef = useModalA11y<HTMLDivElement>(open, handleClose, {
    returnFocus: () => returnFocusRef?.current ?? null,
  })
  const [mounted, setMounted] = useState(open)
  if (open && !mounted) setMounted(true)
  const closing = mounted && !open
  const shown = useRef({ title, message, confirmLabel, cancelLabel, danger, busy })
  if (open) shown.current = { title, message, confirmLabel, cancelLabel, danger, busy }
  useEffect(() => {
    if (!closing) return
    const timer = window.setTimeout(() => setMounted(false), EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [closing])
  if (!mounted) return null
  const view = shown.current
  return createPortal(
    <div
      className={closing ? `${styles.overlay} ${styles.closing}` : styles.overlay}
      onClick={view.busy || closing ? undefined : onCancel}
      {...(closing ? { inert: '' as const } : {})}
    >
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={view.title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <AlertTriangle size={22} className={view.danger ? styles.iconDanger : styles.icon} />
          <h3 className={styles.title}>{view.title}</h3>
        </div>
        <div className={styles.body}>{view.message}</div>
        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} disabled={view.busy} onClick={onCancel}>
            {view.cancelLabel}
          </button>
          <button
            type="button"
            className={view.danger ? styles.confirmDanger : styles.confirm}
            disabled={view.busy}
            onClick={onConfirm}
          >
            {view.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
